import { buttonVariants } from "@patient-watch/ui/button";
import { cn } from "@/lib/utils";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { CalendarClock, ChevronRight, MessageSquare, Sparkles, Star, TrendingUp, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSelectFormById } from "@/features/forms/api/selectFormById";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { useFormPageCopy } from "@/features/i18n/formPageCopy";
import { localizeResultEntryValues } from "@/features/forms/utils/localizeResultEntryValues";
import { usePublicFormSignup } from "@/features/forms/hooks/usePublicFormSignup";
import { FormResultsCard, PlanCard } from "@patient-watch/forms";
import { trackEvent } from "@/utils/trackEvent";
import type { EntryValueViewT } from "@patient-watch/types";
import { Button } from "@patient-watch/ui/button";
import { useAuth } from "@/utils/auth";

const RESULTS_STORAGE_KEY = (formId: string, entryId: string) => `pw_public_form_results:${formId}:${entryId}`;
const CONVERSION_CTA = "Sign up free";

const CompleteResults = () => {
    const { formId, diaryId, trackerId, entryId } = Route.useParams();
    const navigate = useNavigate();
    const { locale } = useAppLocale();
    const auth = useAuth();
    const { data: form } = useSelectFormById(formId, locale);
    const formPageCopy = useFormPageCopy();
    const showConversion = !auth.isAuthenticated;
    const [stored, setStored] = useState<{
        entryValues: EntryValueViewT[];
        diaryId: string;
        formId: string;
    } | null>(null);

    const { handleStartTracking } = usePublicFormSignup(formId, stored?.diaryId);
    const entryValues = useMemo(
        () => (stored ? localizeResultEntryValues(stored.entryValues, form, formPageCopy) : []),
        [stored, form, formPageCopy]
    );

    useEffect(() => {
        try {
            const raw = sessionStorage.getItem(RESULTS_STORAGE_KEY(formId, entryId));
            if (!raw) {
                navigate({
                    to: "/forms/$formId/diary/$diaryId/tracker/$trackerId/entry/$entryId/complete",
                    params: { formId, diaryId, trackerId, entryId },
                });
                return;
            }
            const parsed = JSON.parse(raw) as { entryValues: EntryValueViewT[]; diaryId: string; formId: string };
            setStored(parsed);
            trackEvent("results_view", { form_id: formId, diary_id: parsed.diaryId });
        } catch {
            navigate({
                to: "/forms/$formId/diary/$diaryId/tracker/$trackerId/entry/$entryId/complete",
                params: { formId, diaryId, trackerId, entryId },
            });
        }
    }, [formId, diaryId, trackerId, entryId, navigate]);

    if (!stored) return null;

    return (
        <>
            <section className="box-border flex flex-col gap-y-8 mx-auto w-full min-w-0 max-w-6xl min-h-screen">
                <div className="mx-auto px-2 py-8 w-full min-w-0">
                    <div className={cn("flex flex-col gap-8", showConversion && "lg:flex-row")}>
                        {stored.entryValues.length > 0 && (
                            <div className="w-full min-w-0 max-w-full">
                                <FormResultsCard
                                    formName={form?.form_name ?? undefined}
                                    entryValues={entryValues as unknown[]}
                                />
                            </div>
                        )}

                        {showConversion && (
                            <div className="w-full min-w-0">
                                <PlanCard
                                    badge="premium"
                                    title="Track outcomes over time"
                                    description="Sign up and we will automatically remind your patient via SMS and email - no chasing required"
                                    className="w-full min-h-0"
                                >
                                    <div className="space-y-6 min-w-0">
                                        <div className="flex flex-col gap-2 bg-slate-50 px-4 py-3 border border-slate-200 rounded-lg">
                                            <div className="flex items-center gap-2">
                                                <div className="flex gap-0.5">
                                                    {[1, 2, 3, 4, 5].map((i) => (
                                                        <Star
                                                            key={i}
                                                            size={16}
                                                            className="fill-amber-400 text-amber-400"
                                                        />
                                                    ))}
                                                </div>
                                                <Users size={18} className="text-emerald-600 shrink-0" />
                                            </div>
                                            <p className="text-slate-700 text-sm">
                                                Join leading MSK clinicians gathering their patient outcomes
                                            </p>
                                            <Link
                                                to="/forms/$formId/diary/$diaryId/tracker/$trackerId/entry/$entryId/complete/reviews"
                                                params={{ formId, diaryId, trackerId, entryId }}
                                                className="font-medium text-emerald-600 hover:text-emerald-700 text-sm hover:underline"
                                            >
                                                Read reviews
                                            </Link>
                                        </div>
                                        <div className="inline-flex flex-col gap-2 py-3 rounded-lg">
                                            <div className="flex items-center gap-2">
                                                <CalendarClock size={16} className="text-emerald-600" />
                                                <p className="text-sm">Automated patient reminders (SMS + email)</p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <TrendingUp size={16} className="text-emerald-600" />
                                                <p className="text-sm">Longitudinal charts showing change over time</p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <MessageSquare size={16} className="text-emerald-600" />
                                                <p className="text-sm">Clinic-ready outcome data</p>
                                            </div>
                                        </div>

                                        <button
                                            onClick={handleStartTracking}
                                            className={cn(
                                                buttonVariants({ size: "lg" }),
                                                "flex justify-center items-center gap-2 bg-emerald-600 hover:bg-emerald-700 shadow-md hover:shadow-lg rounded-xl w-full font-semibold text-white transition-colors"
                                            )}
                                        >
                                            <Sparkles className="w-5 h-5" />
                                            {CONVERSION_CTA}
                                        </button>
                                    </div>
                                </PlanCard>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {showConversion && (
                <footer
                    className={cn(
                        "right-0 bottom-0 left-0 z-40 sticky flex sm:flex-row flex-col sm:justify-between sm:items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-3 border-slate-200 border-t h-fit"
                    )}
                >
                    <p className="font-medium text-md text-slate-700 md:text-left text-center">
                        Send this patient SMS and email reminders to complete again in 3 months?
                    </p>

                    <Button
                        onClick={handleStartTracking}
                        className={cn(
                            buttonVariants({ size: "lg" }),
                            "flex justify-center items-center gap-2 bg-emerald-600 hover:bg-emerald-700 shadow-md rounded-xl px-6 font-semibold text-white transition-colors w-full sm:w-auto shrink-0 min-h-12"
                        )}
                    >
                        {CONVERSION_CTA}
                        <ChevronRight className="w-5 h-5" />
                    </Button>
                </footer>
            )}
        </>
    );
};

export const Route = createFileRoute(
    "/forms/$formId/diary/$diaryId/tracker/$trackerId/entry/$entryId/complete/results"
)({
    component: CompleteResults,
});
